import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Copy, Download, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart,
} from 'recharts';
import { toPng } from 'html-to-image';
import { reportAPI } from '../services/api';
import type { DailySummaryReport, TesterAccessReport } from '../types';
import { PLATFORM_COLORS, GAMBLING_COLORS } from '../lib/chartColors';

// JST today / N-days-ago as YYYY-MM-DD (for <input type=date> defaults).
const jstDay = (offsetDays = 0) =>
  new Date(Date.now() + 9 * 3600 * 1000 - offsetDays * 86400000).toISOString().slice(0, 10);

const PLATFORM_LABEL: Record<string, string> = {
  youtube: 'YouTube', facebook: 'Facebook', instagram: 'Instagram',
  x_twitter: 'X', tiktok: 'TikTok', other: 'その他',
};
const GENRE_LABEL: Record<string, string> = {
  public: '公営', pachinko: 'パチンコ', online: '違法/オンライン', game: 'ソシャゲ', gacha: 'ガチャ',
};
const GENRE_COLOR: Record<string, string> = {
  public: GAMBLING_COLORS.gambling_public, pachinko: GAMBLING_COLORS.gambling_pachinko,
  online: GAMBLING_COLORS.gambling_online, game: GAMBLING_COLORS.gambling_game,
  gacha: GAMBLING_COLORS.gambling_gacha_premium,
};
const ACCESS_COLOR: Record<string, string> = {
  web_confirmed: '#22c55e', app_or_uncaptured: '#ef4444', no_sns_activity: '#9ca3af',
};
const ACCESS_LABEL: Record<string, string> = {
  web_confirmed: 'Web確認済み（正しく利用）',
  app_or_uncaptured: 'アプリ等で未取得（要確認）',
  no_sns_activity: 'SNS利用なし',
};
// Ad-type / content-genre labels + palette (広告種別ごとの割合, No.2).
const AD_TYPE_LABEL: Record<string, string> = {
  gambling: 'ギャンブル', culture: '文化・エンタメ', economics: '経済・金融', healthcare: '健康・医療',
  tourism: '旅行・観光', sports: 'スポーツ', education: '教育', politics: '政治', video: '動画コンテンツ', other: 'その他',
};
const AD_TYPE_COLOR: Record<string, string> = {
  gambling: '#ef4444', culture: '#8b5cf6', economics: '#0d9488', healthcare: '#10b981',
  tourism: '#f59e0b', sports: '#3b82f6', education: '#6366f1', politics: '#64748b',
  video: '#ec4899', other: '#9ca3af',
};

export default function Reports() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'daily' | 'access'>('daily');
  const [dateFrom, setDateFrom] = useState(jstDay(13));
  const [dateTo, setDateTo] = useState(jstDay(0));
  const [daily, setDaily] = useState<DailySummaryReport | null>(null);
  const [access, setAccess] = useState<TesterAccessReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copyMsg, setCopyMsg] = useState('');

  // Refs to chart nodes so we can rasterize them (PNG) for Google-Docs copy/export.
  const dailyChartsRef = useRef<HTMLDivElement>(null);
  const accessChartRef = useRef<HTMLDivElement>(null);

  async function load() {
    setLoading(true); setError('');
    try {
      const [d, a] = await Promise.all([
        reportAPI.dailySummary({ dateFrom, dateTo }),
        reportAPI.testerAccess({ dateFrom, dateTo }),
      ]);
      setDaily(d); setAccess(a);
    } catch (e: any) {
      setError(e?.message || 'failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ---- Build a self-contained HTML report fragment for Google Docs (tables + chart PNGs) ----
  async function snapshot(node: HTMLElement | null): Promise<string> {
    if (!node) return '';
    try {
      return await toPng(node, { backgroundColor: '#ffffff', pixelRatio: 2, cacheBust: true });
    } catch { return ''; }
  }

  function dailyHtml(d: DailySummaryReport, chartPng: string): string {
    const tot = d.totals;
    const th = 'style="border:1px solid #cbd5e0;padding:5px 9px;background:#edf2f7;text-align:left;font-size:12px"';
    const td = 'style="border:1px solid #cbd5e0;padding:5px 9px;font-size:12px"';
    const num = 'style="border:1px solid #cbd5e0;padding:5px 9px;font-size:12px;text-align:right"';
    const rows = d.days.map(x => `<tr>
      <td ${td}>${x.day}</td><td ${num}>${x.onlineUsers}</td><td ${num}>${x.setupCompleted}</td>
      <td ${num}>${x.totalAds.toLocaleString()}</td><td ${num}>${x.gamblingAds}</td><td ${num}>${x.gamblingPct}%</td>
      <td ${num}>${x.platforms.youtube}</td><td ${num}>${x.platforms.facebook}</td><td ${num}>${x.platforms.instagram}</td>
      <td ${num}>${x.platforms.x_twitter}</td><td ${num}>${x.platforms.tiktok}</td><td ${num}>${x.platforms.other}</td></tr>`).join('');
    const pg = tot.platformGambling;
    const at = tot.adTypes;
    const adTypeStr = Object.entries(at)
      .filter(([, v]) => (v as number) > 0)
      .sort((a2, b2) => (b2[1] as number) - (a2[1] as number))
      .map(([k, v]) => `${AD_TYPE_LABEL[k] || k} ${v}`).join('・') || '—';
    return `
      <h2 style="font-family:sans-serif;color:#1a365d">SAS 日次サマリーレポート</h2>
      <p style="font-family:sans-serif;color:#4a5568;font-size:12px">対象期間（JST）: ${d.dateFrom} 〜 ${d.dateTo}　作成: ${d.generatedAt}（自動生成・社内テストコード除外）</p>
      <p style="font-family:sans-serif;font-size:13px">
        期間合計: 広告 <b>${tot.totalAds.toLocaleString()}</b> 件 / ギャンブル広告 <b>${tot.gamblingAds}</b> 件（<b>${tot.gamblingPct}%</b>） /
        セットアップ完了 <b>${tot.setupCompleted}</b> 名 / 本日オンライン最大 <b>${tot.peakOnlineUsers}</b> 名<br>
        ギャンブル内訳: 公営 ${tot.genres.public}・パチンコ ${tot.genres.pachinko}・違法/オンライン ${tot.genres.online}・ソシャゲ ${tot.genres.game}・ガチャ ${tot.genres.gacha}<br>
        SNS別ギャンブル広告数: YouTube ${pg.youtube}・Facebook ${pg.facebook}・Instagram ${pg.instagram}・X ${pg.x_twitter}・TikTok ${pg.tiktok}<br>
        広告種別（内容あり広告 ${tot.classifiedAds.toLocaleString()} 件）: ${adTypeStr}
      </p>
      ${chartPng ? `<img src="${chartPng}" style="max-width:720px;width:100%"/>` : ''}
      <table style="border-collapse:collapse;margin-top:10px">
        <tr><th ${th}>日付</th><th ${th}>本日オンライン</th><th ${th}>累計セットアップ</th><th ${th}>広告総数</th><th ${th}>ギャンブル</th><th ${th}>ギャンブル率</th>
        <th ${th}>YouTube</th><th ${th}>Facebook</th><th ${th}>Instagram</th><th ${th}>X</th><th ${th}>TikTok</th><th ${th}>その他</th></tr>
        ${rows}
      </table>`;
  }

  function accessHtml(a: TesterAccessReport, chartPng: string): string {
    const th = 'style="border:1px solid #cbd5e0;padding:5px 9px;background:#edf2f7;text-align:left;font-size:12px"';
    const td = 'style="border:1px solid #cbd5e0;padding:5px 9px;font-size:12px"';
    const num = 'style="border:1px solid #cbd5e0;padding:5px 9px;font-size:12px;text-align:right"';
    const flagged = a.testers.filter(x => x.accessClass === 'app_or_uncaptured');
    const rows = flagged.map(x => `<tr>
      <td ${td}>${x.code}</td><td ${num}>${x.snsAccessEvents.toLocaleString()}</td><td ${num}>${x.quicEvents.toLocaleString()}</td><td ${num}>${x.webSnsAds}</td></tr>`).join('');
    return `
      <h2 style="font-family:sans-serif;color:#1a365d">SAS テスター利用状況レポート（アプリ vs ブラウザ）</h2>
      <p style="font-family:sans-serif;color:#4a5568;font-size:12px">対象期間（JST）: ${a.dateFrom} 〜 ${a.dateTo}　作成: ${a.generatedAt}（社内テストコード除外）</p>
      <p style="font-family:sans-serif;font-size:13px">
        Web確認済み（正しく利用）: <b>${a.summary.web_confirmed}</b> 名 /
        アプリ等で未取得（要確認）: <b style="color:#c53030">${a.summary.app_or_uncaptured}</b> 名 /
        SNS利用なし: <b>${a.summary.no_sns_activity}</b> 名（対象 ${a.summary.total} 名）
      </p>
      ${chartPng ? `<img src="${chartPng}" style="max-width:520px;width:100%"/>` : ''}
      <p style="font-family:sans-serif;font-size:12px;color:#b7791f">※ 「アプリ等で未取得」= SNSへのアクセスは確認できたが広告データを取得できなかったテスター（ネイティブアプリ利用、または設定不備のブラウザの可能性）。要確認対象。</p>
      <h3 style="font-family:sans-serif;color:#2c5282;font-size:14px">要確認テスター一覧（アプリ等で未取得）</h3>
      <table style="border-collapse:collapse">
        <tr><th ${th}>コード</th><th ${th}>SNSアクセス数</th><th ${th}>うちQUIC</th><th ${th}>Web取得広告</th></tr>
        ${rows || `<tr><td ${td} colspan="4">該当なし</td></tr>`}
      </table>
      <p style="font-family:sans-serif;font-size:11px;color:#718096;margin-top:8px">${a.caveat}</p>`;
  }

  async function buildHtml(): Promise<string> {
    if (tab === 'daily' && daily) return dailyHtml(daily, await snapshot(dailyChartsRef.current));
    if (tab === 'access' && access) return accessHtml(access, await snapshot(accessChartRef.current));
    return '';
  }

  async function copyForDocs() {
    setCopyMsg('');
    try {
      const html = await buildHtml();
      if (!html) return;
      const plain = (tab === 'daily' ? 'SAS 日次サマリーレポート' : 'SAS テスター利用状況レポート');
      if (navigator.clipboard && (window as any).ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ]);
        setCopyMsg(t('reports.copied'));
      } else {
        await navigator.clipboard.writeText(html);
        setCopyMsg(t('reports.copied'));
      }
    } catch (e: any) {
      setCopyMsg('Copy failed — use Download instead');
    }
    setTimeout(() => setCopyMsg(''), 4000);
  }

  async function downloadHtml() {
    const html = await buildHtml();
    if (!html) return;
    const full = `<!DOCTYPE html><html lang="ja"><head><meta charset="utf-8"></head><body>${html}</body></html>`;
    const blob = new Blob(['﻿' + full], { type: 'text/html;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${tab === 'daily' ? 'daily_summary' : 'tester_access'}_${dateFrom}_${dateTo}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // ---- chart data ----
  const platformPie = daily ? Object.entries(daily.totals.platforms)
    .map(([k, v]) => ({ name: PLATFORM_LABEL[k] || k, key: k, value: v }))
    .filter(x => x.value > 0) : [];
  const genrePie = daily ? Object.entries(daily.totals.genres)
    .map(([k, v]) => ({ name: GENRE_LABEL[k] || k, key: k, value: v }))
    .filter(x => x.value > 0) : [];
  // No.2 additions: ad-type ratio (content-bearing ads) + per-SNS gambling count.
  const adTypePie = daily ? Object.entries(daily.totals.adTypes)
    .map(([k, v]) => ({ name: AD_TYPE_LABEL[k] || k, key: k, value: v }))
    .filter(x => x.value > 0)
    .sort((a, b) => b.value - a.value) : [];
  const snsGamblingBars = daily ? [
    { name: 'YouTube', key: 'youtube', value: daily.totals.platformGambling.youtube },
    { name: 'Facebook', key: 'facebook', value: daily.totals.platformGambling.facebook },
    { name: 'Instagram', key: 'instagram', value: daily.totals.platformGambling.instagram },
    { name: 'X', key: 'x_twitter', value: daily.totals.platformGambling.x_twitter },
    { name: 'TikTok', key: 'tiktok', value: daily.totals.platformGambling.tiktok },
  ] : [];
  const snsGamblingTotal = snsGamblingBars.reduce((a, b) => a + b.value, 0);
  const accessBars = access ? [
    { name: ACCESS_LABEL.web_confirmed, key: 'web_confirmed', value: access.summary.web_confirmed },
    { name: ACCESS_LABEL.app_or_uncaptured, key: 'app_or_uncaptured', value: access.summary.app_or_uncaptured },
    { name: ACCESS_LABEL.no_sns_activity, key: 'no_sns_activity', value: access.summary.no_sns_activity },
  ] : [];

  const card = 'bg-white dark:bg-warm-gray-800 rounded-xl shadow p-5';
  const kpi = (label: string, value: string | number, color = 'text-blue-600') => (
    <div className={`${card} text-center`}>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="w-7 h-7 text-green-500" />
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('reports.title')}</h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">{t('reports.subtitle')}</p>

      {/* controls */}
      <div className={`${card} mb-6 flex flex-wrap items-end gap-4`}>
        <label className="text-sm">
          <span className="block text-gray-500 mb-1">{t('reports.from')}</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border rounded px-3 py-1.5 dark:bg-warm-gray-700 dark:border-warm-gray-600" />
        </label>
        <label className="text-sm">
          <span className="block text-gray-500 mb-1">{t('reports.to')}</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border rounded px-3 py-1.5 dark:bg-warm-gray-700 dark:border-warm-gray-600" />
        </label>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {t('reports.generate')}
        </button>
        <div className="flex-1" />
        <button onClick={copyForDocs}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded">
          <Copy className="w-4 h-4" /> {t('reports.copyDocs')}
        </button>
        <button onClick={downloadHtml}
          className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded">
          <Download className="w-4 h-4" /> {t('reports.downloadHtml')}
        </button>
        {copyMsg && <span className="text-sm text-green-600 font-semibold self-center">{copyMsg}</span>}
      </div>

      {/* tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200 dark:border-warm-gray-700">
        {(['daily', 'access'] as const).map(tk => (
          <button key={tk} onClick={() => setTab(tk)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === tk
              ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tk === 'daily' ? t('reports.tabDaily') : t('reports.tabAccess')}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded mb-4 text-sm">{error}</div>}
      {loading && <div className="text-gray-500 text-sm mb-4">{t('reports.loading')}</div>}

      {/* ===== DAILY SUMMARY ===== */}
      {tab === 'daily' && daily && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpi(t('reports.kpiTotalAds'), daily.totals.totalAds.toLocaleString())}
            {kpi(t('reports.kpiGambling'), `${daily.totals.gamblingAds} (${daily.totals.gamblingPct}%)`, 'text-red-600')}
            {kpi(t('reports.kpiSetup'), daily.totals.setupCompleted, 'text-violet-600')}
            {kpi(t('reports.kpiPeakOnline'), daily.totals.peakOnlineUsers, 'text-teal-600')}
          </div>

          <div ref={dailyChartsRef} className="space-y-6 bg-white dark:bg-warm-gray-800 rounded-xl p-4">
            <div className={card}>
              <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartDaily')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={daily.days}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="l" dataKey="totalAds" name="広告総数" fill="#2c5282" />
                  <Bar yAxisId="l" dataKey="gamblingAds" name="ギャンブル" fill="#ef4444" />
                  <Line yAxisId="r" dataKey="onlineUsers" name="本日オンライン" stroke="#0d9488" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={card}>
                <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartPlatform')}</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={platformPie} dataKey="value" nameKey="name" outerRadius={90} label>
                      {platformPie.map(e => <Cell key={e.key} fill={PLATFORM_COLORS[e.key] || '#9ca3af'} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className={card}>
                <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartGenre')}</h3>
                {genrePie.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={genrePie} dataKey="value" nameKey="name" outerRadius={90} label>
                        {genrePie.map(e => <Cell key={e.key} fill={GENRE_COLOR[e.key] || '#9ca3af'} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-gray-400 h-[260px] flex items-center justify-center">{t('reports.noGambling')}</div>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={card}>
                <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartAdType', '広告種別の割合（内容あり広告）')}</h3>
                {adTypePie.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={adTypePie} dataKey="value" nameKey="name" outerRadius={90} label>
                        {adTypePie.map(e => <Cell key={e.key} fill={AD_TYPE_COLOR[e.key] || '#9ca3af'} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-gray-400 h-[260px] flex items-center justify-center">{t('reports.loading')}</div>}
              </div>
              <div className={card}>
                <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartSnsGambling', 'SNS別ギャンブル広告数')}</h3>
                {snsGamblingTotal > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={snsGamblingBars} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" name="ギャンブル広告">
                        {snsGamblingBars.map(e => <Cell key={e.key} fill={PLATFORM_COLORS[e.key] || '#9ca3af'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="text-sm text-gray-400 h-[260px] flex items-center justify-center">{t('reports.noGambling')}</div>}
              </div>
            </div>
          </div>

          <div className={`${card} overflow-x-auto`}>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="p-2">{t('reports.thDate')}</th><th className="p-2 text-right">{t('reports.thOnline')}</th>
                <th className="p-2 text-right">{t('reports.thSetup')}</th><th className="p-2 text-right">{t('reports.thAds')}</th>
                <th className="p-2 text-right">{t('reports.thGambling')}</th><th className="p-2 text-right">%</th>
                <th className="p-2 text-right">YT</th><th className="p-2 text-right">FB</th><th className="p-2 text-right">IG</th>
                <th className="p-2 text-right">X</th><th className="p-2 text-right">TT</th><th className="p-2 text-right">{t('reports.thOther')}</th>
              </tr></thead>
              <tbody>
                {daily.days.map(x => (
                  <tr key={x.day} className="border-b last:border-0">
                    <td className="p-2">{x.day}</td><td className="p-2 text-right">{x.onlineUsers}</td>
                    <td className="p-2 text-right">{x.setupCompleted}</td><td className="p-2 text-right">{x.totalAds.toLocaleString()}</td>
                    <td className="p-2 text-right font-semibold text-red-600">{x.gamblingAds}</td><td className="p-2 text-right">{x.gamblingPct}%</td>
                    <td className="p-2 text-right">{x.platforms.youtube}</td><td className="p-2 text-right">{x.platforms.facebook}</td>
                    <td className="p-2 text-right">{x.platforms.instagram}</td><td className="p-2 text-right">{x.platforms.x_twitter}</td>
                    <td className="p-2 text-right">{x.platforms.tiktok}</td><td className="p-2 text-right">{x.platforms.other}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== TESTER ACCESS ===== */}
      {tab === 'access' && access && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {kpi(ACCESS_LABEL.web_confirmed, access.summary.web_confirmed, 'text-green-600')}
            {kpi(ACCESS_LABEL.app_or_uncaptured, access.summary.app_or_uncaptured, 'text-red-600')}
            {kpi(ACCESS_LABEL.no_sns_activity, access.summary.no_sns_activity, 'text-gray-500')}
          </div>

          <div ref={accessChartRef} className="bg-white dark:bg-warm-gray-800 rounded-xl p-4">
            <div className={card}>
              <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.chartAccess')}</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={accessBars} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" name="人数">
                    {accessBars.map(e => <Cell key={e.key} fill={ACCESS_COLOR[e.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-800 rounded">
            {t('reports.accessCaveat')}
          </div>

          <div className={`${card} overflow-x-auto`}>
            <h3 className="font-semibold mb-3 text-gray-700 dark:text-gray-200">{t('reports.flaggedTitle')}</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="p-2">{t('reports.thCode')}</th><th className="p-2">{t('reports.thName')}</th>
                <th className="p-2 text-right">{t('reports.thAccess')}</th><th className="p-2 text-right">QUIC</th>
                <th className="p-2 text-right">{t('reports.thWebAds')}</th><th className="p-2">{t('reports.thClass')}</th>
              </tr></thead>
              <tbody>
                {access.testers.filter(x => x.accessClass === 'app_or_uncaptured').map(x => (
                  <tr key={x.code} className="border-b last:border-0">
                    <td className="p-2 font-mono">{x.code}</td><td className="p-2">{x.memberName}</td>
                    <td className="p-2 text-right">{x.snsAccessEvents.toLocaleString()}</td>
                    <td className="p-2 text-right">{x.quicEvents.toLocaleString()}</td>
                    <td className="p-2 text-right">{x.webSnsAds}</td>
                    <td className="p-2"><span className="text-red-600 font-semibold">{ACCESS_LABEL.app_or_uncaptured}</span></td>
                  </tr>
                ))}
                {access.testers.filter(x => x.accessClass === 'app_or_uncaptured').length === 0 && (
                  <tr><td className="p-2 text-gray-400" colSpan={6}>{t('reports.noFlagged')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
